/// <amd-module name="Comments/Redux/CommentsSaga" />
import { CommentExpandOptions, ICommentReaction, ICommentsClient } from "Comments/Common/Contracts";
import { CommentsActions, CommentsActionTypes } from "Comments/Redux/CommentsActions";
import { ICommentsClientServiceInfo } from "Comments/Redux/Contracts";
import { SocialEngagementType } from "Likes/Contracts/Social";
import { ISocialRestClient } from "Likes/RestClient/Social";
import { effects, SagaIterator } from "redux-saga";
import { ActionsOfType } from "redux/Utils/ActionHelper";
import { RT } from "redux/Utils/SagaHelper";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssPageService } from "VSS/Platform/Page";

export function* rootCommentsSaga() {
    yield effects.takeEvery(CommentsActionTypes.StartDeleteItem, _startDeleteItem);
    yield effects.takeEvery(CommentsActionTypes.StartUpdateItem, _startUpdateItem);
    yield effects.takeEvery(CommentsActionTypes.StartGetItem, _startGetItem);
    yield effects.takeEvery(CommentsActionTypes.StartUpdatePendingReactions, _startUpdatePendingCommentReactions);
    yield effects.takeEvery(CommentsActionTypes.StartUpdateReaction, _startUpdateCommentReaction);
    yield effects.takeEvery(CommentsActionTypes.StartGetRecentlyReactedUsers, _startGetRecentlyReactedUsers);
}

export function* _startDeleteItem(action: ActionsOfType<CommentsActions, CommentsActionTypes.StartDeleteItem>): SagaIterator {
    const { artifactInfo, commentId } = action.payload;

    const client: ICommentsClient = (yield effects.call(_getClient)) as any;
    try {
        yield effects.call([client, client.deleteComment], artifactInfo, commentId);
        yield effects.put(CommentsActions.deleteItem(artifactInfo, commentId));
    } catch (error) {
        if (error.status === 404) {
            yield effects.put(CommentsActions.deleteItem(artifactInfo, commentId));
            return;
        }

        throw error;
    }
}

export function* _startUpdateItem(action: ActionsOfType<CommentsActions, CommentsActionTypes.StartUpdateItem>): SagaIterator {
    const { artifactInfo, commentId, newValue } = action.payload;

    const client: ICommentsClient = (yield effects.call(_getClient)) as any;
    try {
        const comment: RT<typeof client.updateComment> = (yield effects.call(
            [client, client.updateComment],
            artifactInfo,
            commentId,
            newValue
        )) as any;
        yield effects.put(CommentsActions.updateItem(artifactInfo, comment));
    } catch (error) {
        if (error.status === 404) {
            yield effects.put(CommentsActions.deleteItem(artifactInfo, commentId));
            return;
        }

        throw error;
    }
}

export function* _startGetItem(action: ActionsOfType<CommentsActions, CommentsActionTypes.StartGetItem>): SagaIterator {
    const { artifactInfo, commentId, isEditing } = action.payload;

    const client: ICommentsClient = (yield effects.call(_getClient)) as any;
    const includeDeleted = true;
    const comment: RT<typeof client.getComment> = (yield effects.call(
        [client, client.getComment],
        artifactInfo,
        commentId,
        includeDeleted,
        CommentExpandOptions.Reactions
    )) as any;
    yield effects.put(CommentsActions.addItem(artifactInfo, comment, isEditing));
}

export function* _startUpdatePendingCommentReactions(
    action: ActionsOfType<CommentsActions, CommentsActionTypes.StartUpdatePendingReactions>
): SagaIterator {
    const { artifactInfo, commentId, reactionTypePendingActionMap } = action.payload;

    const pendingActionsEffects: effects.PutEffect[] = [];
    Object.keys(reactionTypePendingActionMap).forEach(reactionType => {
        const reactionTypeNum = Number(reactionType);
        const isAdded = reactionTypePendingActionMap[reactionTypeNum];
        pendingActionsEffects.push(
            effects.put(CommentsActions.startUpdateCommentReaction(artifactInfo, commentId, reactionTypeNum, isAdded, false))
        );
    });

    yield effects.all(pendingActionsEffects); // start all pending actions in parallel
    yield effects.put(CommentsActions.pendingReactionActionCallsInitiated(artifactInfo, commentId));
}

export function* _startUpdateCommentReaction(
    action: ActionsOfType<CommentsActions, CommentsActionTypes.StartUpdateReaction>
): SagaIterator {
    const { artifactInfo, commentId, reactionType, isAdded, updateLocalStoreFirst } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const vssPageService = pageContext.getService<IVssPageService>("IVssPageService");
    const currentUser = vssPageService.getData()!.user;

    if (updateLocalStoreFirst) {
        const localReactionUpdateResponse: ICommentReaction = {
            commentId,
            count: 0,
            type: reactionType,
            isCurrentUserEngaged: isAdded
        };
        yield effects.put(CommentsActions.updateReaction(artifactInfo, localReactionUpdateResponse, currentUser, isAdded, true));
    }

    const client: ICommentsClient = (yield effects.call(_getClient)) as any;
    let reactionResponse: RT<typeof client.addCommentReaction | typeof client.deleteCommentReaction> = (yield effects.call(
        [client, isAdded ? client.addCommentReaction : client.deleteCommentReaction],
        artifactInfo,
        commentId,
        reactionType
    )) as any;
    yield effects.put(CommentsActions.updateReaction(artifactInfo, reactionResponse, currentUser, isAdded, false));
}

export function* _startGetRecentlyReactedUsers(
    action: ActionsOfType<CommentsActions, CommentsActionTypes.StartGetRecentlyReactedUsers>
): SagaIterator {
    const { artifactInfo, commentId, reactionType, top } = action.payload;

    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const client: ISocialRestClient = pageContext.getRestClient<ISocialRestClient>("ISocialRestClient");
    const engagedUsers: RT<typeof client.getEngagedUsers> = (yield effects.call(
        [client, client.getEngagedUsers],
        "PlatformComment",
        commentId.toString(),
        (reactionType as number) as SocialEngagementType,
        "Collection",
        "",
        top
    )) as any;

    yield effects.put(CommentsActions.addRecentlyReactedUsers(artifactInfo, commentId, reactionType, engagedUsers));
}

export function* _getClient(): SagaIterator {
    const commentsClient: ICommentsClient = (yield effects.getContext("commentsClient")) as any;
    if (commentsClient) {
        return commentsClient;
    }

    const commentsClientServiceInfo: ICommentsClientServiceInfo = (yield effects.getContext("commentsClientServiceInfo")) as any;
    if (!commentsClientServiceInfo) {
        throw new Error(`Either commentsClient or commentsClientServiceInfo needs to be specified as in the saga context.`);
    }

    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const contributionService: IVssContributionService = (yield effects.call(
        [pageContext, pageContext.getService],
        "IVssContributionService"
    )) as any;
    yield effects.call([contributionService, contributionService.getContributionAsync], commentsClientServiceInfo.contributionId);

    return yield effects.call([pageContext, pageContext.getService], commentsClientServiceInfo.serviceName);
}
